import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ActivityResponse } from '@troia-v3/types';
import { isPast, isToday, isValid, format, isThisYear } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui';
import { Icon } from '@/shared/icons'
interface ActivityPanelProps {
  /**
   * Ref do elemento trigger (o wrapper/badge que abre o painel). Usado para
   * ancorar o painel via position:fixed e para o guard de clique-fora.
   */
  triggerRef: React.RefObject<HTMLElement | null>;
  activities: ActivityResponse[];
  total: number;
  onClose: () => void;
  onLeadClick?: (leadId: string) => void;
}

/** Largura fixa do painel (px) — mantém coerência com o `w-[450px]` do container. */
const PANEL_WIDTH = 450;

/**
 * Safely parse a date value
 */
const parseDate = (dateValue: string | Date | undefined): Date | null => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  return isValid(date) ? date : null;
};

/**
 * Get the effective date for an activity (dueDate with occurredAt fallback)
 */
const getEffectiveDate = (activity: ActivityResponse): Date | null => {
  return parseDate(activity.dueDate) || parseDate(activity.occurredAt);
};

/**
 * Format a date for display
 */
const formatActivityDate = (date: Date | null): string | null => {
  if (!date) return null;
  if (isToday(date)) return 'Hoje';
  return isThisYear(date)
    ? format(date, 'dd/MM')
    : format(date, 'dd/MM/yy');
};

/**
 * Get initials from a name (up to 2 characters)
 */
const getInitials = (name: string | undefined): string => {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/**
 * Get the first name from a full name
 */
const getFirstName = (name: string | undefined): string => {
  if (!name) return '';
  const firstName = name.trim().split(/\s+/)[0];
  return firstName.length > 12 ? firstName.substring(0, 12) + '...' : firstName;
};

/**
 * Generate a consistent color based on a string (for avatars)
 */
const getAvatarColor = (str: string | undefined): string => {
  if (!str) return '#6b7280';
  const colors = [
    '#ef4444', '#f97316', '#f59e0b', '#84cc16',
    '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6',
    '#6366f1', '#8b5cf6', '#a855f7', '#ec4899'
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Activity dropdown panel
 * Shows pending activities the user needs to complete
 * Uses always-light theme (hardcoded colors)
 */
export const ActivityPanel: React.FC<ActivityPanelProps> = ({
  triggerRef,
  activities,
  total,
  onClose,
  onLeadClick
}) => {
  const navigate = useNavigate();
  // "Ver todas" leva à Agenda — só para quem pode abri-la (lib/route-access.ts).
  // O painel em si é de todos; 205 pessoas em produção não têm calendar:read.
  const { canAccessPath } = usePermissions();
  const canOpenCalendar = canAccessPath('/calendar');
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  // Posiciona o painel via position:fixed a partir do trigger (rodapé da sidebar):
  // abre para CIMA (base do painel perto do trigger) e à DIREITA (fora da sidebar).
  useLayoutEffect(() => {
    const computePosition = () => {
      const triggerEl = triggerRef.current;
      const panelEl = panelRef.current;
      if (!triggerEl) return;

      const triggerRect = triggerEl.getBoundingClientRect();
      const panelWidth = panelEl?.offsetWidth || PANEL_WIDTH;
      const panelHeight = panelEl?.offsetHeight || 0;

      // Flyout à direita da SIDEBAR (estilo submenu) quando o trigger vive nela; senão à direita do ícone.
      const sidebarRoot = triggerEl.closest('[data-sidebar-root]');
      let left = (sidebarRoot ? sidebarRoot.getBoundingClientRect().right : triggerRect.right) + 8;
      left = Math.min(left, window.innerWidth - panelWidth - 8);
      left = Math.max(left, 8);

      // Abre para cima: base do painel ancorada ao trigger; clamp no topo/rodapé.
      let top = triggerRect.top;
      top = Math.min(Math.max(top, 8), window.innerHeight - panelHeight - 8);

      setPosition({ left, top });
    };

    computePosition();
    window.addEventListener('resize', computePosition);
    window.addEventListener('scroll', computePosition, true);
    return () => {
      window.removeEventListener('resize', computePosition);
      window.removeEventListener('scroll', computePosition, true);
    };
  }, [triggerRef, activities.length]);

  // Clique-fora portalado: fecha se o mousedown não caiu no painel nem no trigger.
  // (o guard do trigger evita o toggle reabrir logo após fechar)
  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [triggerRef, onClose]);

  const handleActivityClick = (activity: ActivityResponse) => {
    // Navigate to the entity that has the activity
    if (activity.entityType && activity.entityId) {
      // Lead: usar callback onLeadClick se disponível (abre modal direto)
      if (activity.entityType === 'lead' && onLeadClick) {
        onLeadClick(activity.entityId);
        onClose();
        return;
      }

      let route: string | null = null;

      if (activity.entityType === 'lead') {
        // Lead navigation fallback: /crm/{funnelId}?leadId={leadId}&stepId={stepId}
        // metadata é Record<string, unknown>; tratamos apenas strings conhecidas.
        const funnelId = typeof activity.metadata?.funnelId === 'string' ? activity.metadata.funnelId : undefined;
        const stepId = typeof activity.metadata?.stepId === 'string' ? activity.metadata.stepId : undefined;

        if (funnelId) {
          const params = new URLSearchParams();
          params.set('leadId', activity.entityId);
          if (stepId) {
            params.set('stepId', stepId);
          }
          route = `/crm/${funnelId}?${params.toString()}`;
        } else {
          // Fallback sem funnelId: /crm/leads/:id não existe no router — o
          // CrmDashboardPage lê ?leadId= e abre o drawer do lead.
          route = `/crm?leadId=${activity.entityId}`;
        }
      } else {
        // Other entity types
        const routes: Record<string, string> = {
          contact: `/crm/contacts/${activity.entityId}`,
          opportunity: `/crm/opportunities/${activity.entityId}`,
        };
        route = routes[activity.entityType] || null;
      }

      if (route) {
        navigate(route);
        onClose();
      }
    }
  };

  // F63: tokens do tema (nunca hex fixo) — badges de status funcionam em light e dark.
  const getStatusInfo = (activity: ActivityResponse): { color: string; label: string; bgColor: string } => {
    const effectiveDate = getEffectiveDate(activity);

    if (!effectiveDate) {
      return {
        color: 'var(--theme-muted-text)',
        bgColor: 'var(--theme-surface-sunken)',
        label: 'Pendente'
      };
    }

    if (isPast(effectiveDate) && !isToday(effectiveDate)) {
      return {
        color: 'var(--theme-error-color)',
        bgColor: 'var(--theme-error-subtle)',
        label: 'Atrasada'
      };
    }
    if (isToday(effectiveDate)) {
      // Show "Concluída" or "Pendente" based on actual status
      if (activity.status === 'completed') {
        return {
          color: 'var(--theme-success-color)',
          bgColor: 'var(--theme-success-subtle)',
          label: 'Concluída'
        };
      }
      return {
        color: 'var(--theme-warning-color)',
        bgColor: 'var(--theme-warning-subtle)',
        label: 'Pendente'
      };
    }
    return {
      color: 'var(--theme-primary)',
      bgColor: 'var(--theme-primary-background)',
      label: 'Pendente'
    };
  };

  const getEntityLabel = (entityType: string): string => {
    const labels: Record<string, string> = {
      contact: 'Contato',
      lead: 'Lead',
      opportunity: 'Oportunidade',
    };
    return labels[entityType] || entityType;
  };

  return createPortal(
    /* Painel portalado no body (position:fixed) — escapa do stacking context
       da Sidebar e do overflow-hidden do MainLayout. Abre para cima/direita.
       Clique-fora tratado pelo listener de mousedown acima (sem backdrop). */
    <div
      ref={panelRef}
      className="w-[450px] rounded-lg shadow-xl"
      style={{
        position: 'fixed',
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? 'visible' : 'hidden',
        zIndex: 1000,
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        maxHeight: '600px'
      }}
    >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: '#e5e7eb' }}
        >
          <div className="flex items-center gap-2">
            <h3
              className="font-semibold text-sm"
              style={{ color: '#1e293b' }}
            >
              Atividades Pendentes
            </h3>
            {total > 0 && (
              <span
                className="px-2 py-0.5 text-xs font-medium rounded-full"
                style={{
                  backgroundColor: '#f97316',
                  color: '#ffffff'
                }}
              >
                {total}
              </span>
            )}
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            <Icon name="x" className="w-4 h-4" />
          </button>
        </div>

        {/* Activities List */}
        <div
          className="overflow-y-auto"
          style={{ maxHeight: '500px' }}
        >
          {activities.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Icon name="calendar" className="w-12 h-12 mx-auto mb-3" style={{ color: '#d1d5db' }} />
              <p className="text-sm font-medium" style={{ color: '#64748b' }}>
                Nenhuma atividade pendente
              </p>
              <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>
                Suas atividades aparecerão aqui
              </p>
            </div>
          ) : (
            activities.map((activity) => {
              const statusInfo = getStatusInfo(activity);

              return (
                <div
                  key={activity.id}
                  className="border-b transition-colors cursor-pointer"
                  style={{
                    backgroundColor: 'transparent',
                    borderColor: '#e5e7eb'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={() => handleActivityClick(activity)}
                >
                  <div className="px-4 py-3 flex gap-3">
                    {/* Avatar + First Name for Lead activities */}
                    {activity.entityType === 'lead' && activity.entityName && (
                      <div className="flex-shrink-0 flex flex-col items-center" style={{ width: '52px' }}>
                        <Avatar className="w-10 h-10" title={activity.entityName} isSquircle>
                          <AvatarImage
                            src={activity.entityPicture}
                            alt={activity.entityName}
                            isSquircle
                          />
                          <AvatarFallback
                            style={{ backgroundColor: getAvatarColor(activity.entityName) }}
                            isSquircle
                          >
                            <span className="text-white text-sm font-medium">
                              {getInitials(activity.entityName)}
                            </span>
                          </AvatarFallback>
                        </Avatar>
                        <span
                          className="text-xs mt-1 truncate text-center w-full"
                          style={{ color: '#6b7280' }}
                          title={activity.entityName}
                        >
                          {getFirstName(activity.entityName)}
                        </span>
                      </div>
                    )}

                    {/* Activity Content */}
                    <div className="flex-1 min-w-0">
                      {/* Header Row: Type + Status */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {/* Activity Type Badge */}
                          <span
                            className="px-2 py-0.5 text-xs font-medium rounded"
                            style={{
                              backgroundColor: '#e0e7ff',
                              color: '#4f46e5'
                            }}
                          >
                            {activity.type}
                          </span>

                          {/* Entity Type (hide for leads since we show avatar) */}
                          {activity.entityType && activity.entityType !== 'lead' && (
                            <span
                              className="text-xs"
                              style={{ color: '#9ca3af' }}
                            >
                              {getEntityLabel(activity.entityType)}
                            </span>
                          )}
                        </div>

                        {/* Status Badge + Date */}
                        <div className="flex items-center gap-1.5">
                          {(() => {
                            const effectiveDate = getEffectiveDate(activity);
                            const formattedDate = formatActivityDate(effectiveDate);
                            return formattedDate && formattedDate !== 'Hoje' ? (
                              <span
                                className="flex items-center gap-1 text-xs"
                                style={{ color: '#9ca3af' }}
                              >
                                <Icon name="calendar" className="w-3 h-3" />
                                {formattedDate}
                              </span>
                            ) : null;
                          })()}
                          <span
                            className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded"
                            style={{
                              backgroundColor: statusInfo.bgColor,
                              color: statusInfo.color
                            }}
                          >
                            {statusInfo.label === 'Atrasada' && (
                              <Icon name="alert-circle" className="w-3 h-3" />
                            )}
                            {statusInfo.label === 'Concluída' && (
                              <Icon name="check-circle" className="w-3 h-3" />
                            )}
                            {statusInfo.label}
                          </span>
                        </div>
                      </div>

                      {/* Title */}
                      <h4
                        className="text-sm font-medium mb-1"
                        style={{ color: '#1e293b' }}
                      >
                        {activity.title}
                      </h4>

                      {/* Description */}
                      {activity.description && (
                        <p
                          className="text-sm mb-2 line-clamp-2"
                          style={{ color: '#64748b' }}
                        >
                          {activity.description}
                        </p>
                      )}

                      {/* Entity Name for non-lead activities */}
                      {activity.entityType !== 'lead' && activity.entityName && (
                        <p
                          className="text-xs truncate mt-1"
                          style={{ color: '#9ca3af' }}
                          title={activity.entityName}
                        >
                          {activity.entityName}
                        </p>
                      )}

                      {/* View Link */}
                      {activity.entityType && activity.entityId && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleActivityClick(activity);
                          }}
                          className="flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                          style={{
                            backgroundColor: '#fff7ed',
                            color: '#ea580c'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#ffedd5';
                            e.currentTarget.style.color = '#c2410c';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff7ed';
                            e.currentTarget.style.color = '#ea580c';
                          }}
                        >
                          <Icon name="external-link" className="w-3 h-3" />
                          Ver {getEntityLabel(activity.entityType).toLowerCase()}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        {activities.length > 0 && canOpenCalendar && (
          <div
            className="px-4 py-3 border-t text-center"
            style={{
              backgroundColor: '#f9fafb',
              borderColor: '#e5e7eb'
            }}
          >
            <a
              href="/calendar?type=activity"
              className="text-sm font-medium transition-colors"
              style={{ color: '#ea580c' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#c2410c';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#ea580c';
              }}
            >
              Ver todas as atividades
            </a>
          </div>
        )}
      </div>,
    document.body
  );
};
